import { Component, OnInit, AfterViewInit, OnDestroy, inject, DestroyRef, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';

import { delay, filter, noop, tap } from 'rxjs';
import { datadogRum } from '@datadog/browser-rum';
import { environment } from '@env/environment';
import { VersionCheckService, PusherService, HubspotChatbotService } from 'app/core';
import { GlobalJobStatusService } from '@core/services/global-job-status.service';
import { TsSnackbarService } from 'tsui';
import { RelatedToType } from '@shared/enums';
import { BulkEditModalComponent } from '@shared/modals/bulk-edit-modal/bulk-edit-modal.component';
import { Store } from '@ngxs/store';
import { SetUserStyleSettings } from './state/user/actions';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styles: [
    `
      :host {
        display: flex;
        flex: 1 1 auto;
        width: 100%;
        height: 100%;
      }
    `,
  ],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  public showProgressBar: WritableSignal<boolean> = signal(false);

  private readonly _router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _pusherService = inject(PusherService);
  private readonly _versionCheckService = inject(VersionCheckService);
  private readonly _hubspotChatbotService = inject(HubspotChatbotService);
  private readonly _globalJobStatusService = inject(GlobalJobStatusService);
  private readonly _snackbarService = inject(TsSnackbarService);
  private readonly store = inject(Store);

  ngOnInit(): void {
    this._pusherService.registerForPusher();
    this._hubspotChatbotService.loadChatbotScript();
    this.toggleGlobalNavigationLoading();
    this.checkForPendingBulkEdit();

    if (['qaUI', 'stagingUI', 'productionUI'].includes(environment.env)) {
      if (environment.production) {
        datadogRum.init(environment.dataDog);
      }
    }

    this.executeVersionCheck();

    // Segment/Intercom Analytics
    this._router.events.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        analytics.page(event.url);
      }

      if (event instanceof NavigationEnd) {
        //adding this console.log to check if the version check is working on qa environment
        this.executeVersionCheck();
      }
    });

    const styleSettings = localStorage.getItem('style-settings');
    if (styleSettings) {
      document.body.classList.add(styleSettings);
      this.store.dispatch(new SetUserStyleSettings(styleSettings as 'compact' | 'small' | 'normal'));
    }
  }

  ngAfterViewInit(): void {
    this._globalJobStatusService.initializeListeners();
  }

  private executeVersionCheck(): void {
    if (environment.production) {
      this._versionCheckService.initVersionCheck(environment.versionCheckURL);
    }

    if (['qaUI', 'stagingUI', 'productionUI'].includes(environment.env)) {
      if (environment.production) {
        this._versionCheckService.initVersionCheck(environment.versionCheckURL);
      }
    }
  }

  ngOnDestroy(): void {
    this._globalJobStatusService.cleanup();
  }

  private toggleGlobalNavigationLoading(): void {
    this._router.events
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        delay(100),
        filter(
          (event) =>
            event instanceof NavigationStart ||
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError,
        ),
        tap((event) => {
          if (event instanceof NavigationStart) this.showProgressBar.set(true);
          else this.showProgressBar.set(false);
        }),
      )
      .subscribe(noop);
  }

  private checkForPendingBulkEdit(): void {
    const storedChannelData = localStorage.getItem(BulkEditModalComponent.BULK_EDIT_CHANNEL_KEY);
    if (storedChannelData) {
      try {
        const channelData = JSON.parse(storedChannelData);
        const { channelName, relatedTo } = channelData;
        
        if (channelName) {
          // Subscribe to the stored channel
          this._pusherService
            .listenToChannel<{
              status: string;
              message?: string;
              results?: Array<{ success: boolean; project_id: number; message: string }>;
            }>({
              channelName,
              eventName: 'progress',
            })
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((data) => {
              if (data.status === 'Complete') {
                const successful = data.results?.filter((result) => result.success) || [];
                const failed = data.results?.filter((result) => !result.success) || [];
                
                localStorage.removeItem(BulkEditModalComponent.BULK_EDIT_CHANNEL_KEY);

                if (failed.length === 0) {
                  this._snackbarService.open('Bulk update finished successfully', 'info', null, {
                    duration: 5000,
                  });
                } else if (successful.length === 0) {
                  this._snackbarService.open('Bulk update failed for all items', 'error', null, {
                    duration: 5000,
                  });
                } else {
                  this._snackbarService.open(
                    `Bulk update finished: ${successful.length} succeeded, ${failed.length} failed`,
                    'warning',
                    null,
                    {
                      duration: 5000,
                    },
                  );
                }
              } else if (data.status === 'An error occurred') {
                localStorage.removeItem(BulkEditModalComponent.BULK_EDIT_CHANNEL_KEY);
                this._snackbarService.open('Bulk update failed', 'error', null, {
                  duration: 5000,
                });
              } else {
                this._snackbarService.open(data.status || 'Bulk update in progress...', 'info', null, {
                  duration: 3000,
                });
              }
            });
        }
      } catch (error) {
        console.error('Error parsing stored bulk edit channel data:', error);
        localStorage.removeItem(BulkEditModalComponent.BULK_EDIT_CHANNEL_KEY);
      }
    }
  }
}
